import { FieldVariant } from '@/components/form-builder/form-builder.types';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { DacDocsFormResponse, DacFormFields, DynaDocDetails, DynaFieldType, DynaFieldWithDetails, FieldsDetail } from '@/types';
import { object } from '@/utils/object.utils';
import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DacTransformationService {
  dacRepository = inject(DacRepository);
  fileService = inject(FileService);

  initializeForm(dacId: number, documents: DynaDocDetails[], validationValues: Record<number, DacDocsFormResponse[]>): Observable<void> {
    const tasks: Observable<any>[] = [];

    documents.forEach(doc => {
      if (doc.isStatic) {
        if (doc.docs) {
          const task$ = this.fileService
            .getFile(
              {
                alfresco_id: doc.docs?.alfresco_id,
                filename: doc.docs?.alfrescoName,
                extension: doc.docs?.fileExtension,
                size: doc.docs?.fileSize,
              },
              NAMESPACE_ENUM.DAC
            )
            .pipe(
              tap(file => {
                if (file) {
                  this.dacRepository.updateStaticFile(doc.id, file);
                }
              })
            );
          tasks.push(task$);
        }
      } else {
        doc.dynaFields.forEach(field => {
          // build form object (sync part)
          if (doc.id && !this.dacRepository.doesFieldExist(field.dynaFieldsId)) {
            this.dacRepository.addDacFormField({
              dacId,
              dynaFieldsId: field.dynaFieldsId,
              dynaDocsId: doc.id,
              fieldValue: '',
              sourceId: '',
              details: [],
            });
          }
          let value: any = this.getFieldDefaultValue(field, validationValues[doc.id]) ?? '';
          if (field.type === DynaFieldType.LIST) {
            this.onListChange(value, field);
            value = '';
          } else if (field.type === DynaFieldType.TABLE) {
            const structure = this.getTableStructure(field);
            this.onTableChange(value, structure, field);
            value = '';
          }
          this.dacRepository.updateFieldValue(field.dynaFieldsId, value);
        });
        const initialFormFields = this.dacRepository.get<DacFormFields[]>('dacFormFields');
        this.dacRepository.set('initialDacFormFields', structuredClone(initialFormFields));
      }
    });

    return tasks.length ? forkJoin(tasks).pipe(map(() => void 0)) : of(void 0);
  }

  getFieldVariant(dynaFieldType: DynaFieldType): FieldVariant {
    switch (dynaFieldType) {
      case DynaFieldType.TEXT:
      case DynaFieldType.SYSTEM:
        return FieldVariant.TEXT;
      case DynaFieldType.INTEGER:
        return FieldVariant.NUMBER;
      case DynaFieldType.DATE:
      case DynaFieldType.DATETIME:
        return FieldVariant.DATE;
      case DynaFieldType.TABLE:
        return FieldVariant.CUSTOM;
      case DynaFieldType.LIST:
        return FieldVariant.TEXT_GROUPS;
      default:
        return FieldVariant.TEXT;
    }
  }

  getFieldValue(dynaField: DynaFieldWithDetails, validatipnValues: DacDocsFormResponse[]) {
    return validatipnValues.find(v => v.dynaFieldId === dynaField.dynaFieldsId)?.fieldValue;
  }

  getFieldDefaultValue(dynaField: DynaFieldWithDetails, validationValues: DacDocsFormResponse[]) {
    const value = this.getFieldValue(dynaField, validationValues);
    switch (dynaField.type) {
      case DynaFieldType.INTEGER:
        return Number(value);
      case DynaFieldType.LIST:
        return this.getListValues(dynaField, validationValues);
      case DynaFieldType.TABLE:
        return this.getTableValues(dynaField, validationValues);
      default:
        return value;
    }
  }

  getListValues(dynaField: DynaFieldWithDetails, validationValues: DacDocsFormResponse[]): string[] {
    const dacDocDetails = validationValues.find(v => v.dynaFieldId === dynaField.dynaFieldsId)?.dacDocDetails ?? [];
    const sortedDetails = dacDocDetails.sort((a, b) => a.rowId - b.rowId);
    return sortedDetails.map(d => d.fieldValue);
  }

  getTableValues(dynaField: DynaFieldWithDetails, validationValues: DacDocsFormResponse[]): Record<string, any>[] {
    const dacDocDetails = validationValues.find(v => v.dynaFieldId === dynaField.dynaFieldsId)?.dacDocDetails ?? [];

    // Sort by rowId ascending, then by dynaFieldsDetailsId ascending
    const sortedDetails = [...dacDocDetails].sort((a, b) => {
      if (a.rowId !== b.rowId) return a.rowId - b.rowId;
      return a.columnPos - b.columnPos;
    });

    const cols = dynaField.dynaFieldsDetails?.length ?? 0;
    const rows: Record<string, any>[] = [];

    for (let i = 0; i < sortedDetails.length; i += cols) {
      const row: Record<string, any> = {};
      for (let j = 0; j < cols; j++) {
        const colName = dynaField.dynaFieldsDetails?.[j]?.columnName.toLowerCase() ?? `col${j}`;
        row[colName] = sortedDetails[i + j]?.fieldValue ?? '';
      }
      rows.push(row);
    }

    return rows;
  }

  onListChange(values: string[], dynaField: DynaFieldWithDetails) {
    const formField = this.dacRepository.get<DacFormFields[]>('dacFormFields').find(f => f.dynaFieldsId === dynaField.dynaFieldsId);
    if (!formField) return;
    const current = formField.details ?? [];
    const updated: FieldsDetail[] = values.map((v, idx) => {
      return current[idx]
        ? { ...current[idx], fieldValue: v }
        : {
            rowId: idx + 1,
            dynaFieldsDetailsId: dynaField.dynaFieldsDetails?.[0].dynaFieldsDetailsId,
            sourceId: '',
            fieldValue: v,
          };
    });
    this.dacRepository.updateFieldDetails(dynaField.dynaFieldsId, updated);
  }

  onTableChange(
    rows: Record<string, any>[],
    structure: Record<
      string,
      {
        type: string;
        label: string;
        columnPos: number;
      }
    >,
    dynaField: DynaFieldWithDetails
  ) {
    const formField = this.dacRepository.get<DacFormFields[]>('dacFormFields').find(f => f.dynaFieldsId === dynaField.dynaFieldsId);
    if (!formField) return;

    const current = formField.details ?? [];
    const cols = Object.keys(structure);

    const flatValues = object.flattenRows(rows, cols);

    const updated: FieldsDetail[] = flatValues.map((v, idx) => {
      return current[idx]
        ? { ...current[idx], fieldValue: v }
        : {
            rowId: Math.floor(idx / cols.length) + 1,
            dynaFieldsDetailsId: dynaField.dynaFieldsDetails?.[idx % cols.length]?.dynaFieldsDetailsId,
            sourceId: '',
            fieldValue: v,
          };
    });

    this.dacRepository.updateFieldDetails(dynaField.dynaFieldsId, updated);
  }

  getTableStructure(dynaField: DynaFieldWithDetails) {
    const structure = dynaField?.dynaFieldsDetails
      ?.sort((a, b) => a.columnPos - b.columnPos)
      .reduce((acc: Record<string, { type: string; label: string; columnPos: number }>, field) => {
        acc[field.columnName.toLowerCase()] = {
          type: field.fieldType === 'TEXT' ? 'string' : 'number',
          label: field.columnName,
          columnPos: field.columnPos,
        };
        return acc;
      }, {});
    return structure;
  }
}
